import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  Outlet,
  redirect,
  useRouter,
  useRouterState,
  type RouterHistory,
} from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';
import { Button, LoadingState } from '@tessma/design-system';
import { useAsync } from './ui/async.js';
import { ApplicationShell } from './shell/ApplicationShell.js';
import { ShellBridgeProvider, useShellBridge } from './shell/shell-bridge.js';
import { FROZEN_NAVIGATION, pageIdentityFor, pathFor } from './shell/navigation.js';
import {
  NotAuthenticatedError,
  RouteForbiddenError,
  RouteNotFoundError,
  guardRoute,
  reachableNavigationModules,
  requireAuthenticated,
  skuForNavigation,
} from './session/guards.js';
import type { SessionGateway, SessionState } from './session/session.js';
import { ForbiddenPage, ModuleLanding, NotFoundPage, SignInPage } from './routes/pages.js';
import { createWorkGateway } from './work/gateway.js';
import { createAnalyticsGateway } from './analytics/gateway.js';
import { createDashboardGateway } from './dashboard/gateway.js';
import { DashboardScreen } from './dashboard/screens.js';
import { createAiGateway } from './ai/gateway.js';
import { createFinanceGateway } from './finance/gateway.js';
import { createPartiesGateway } from './parties/gateway.js';
import { CatalogueScreen, PartiesScreen } from './parties/screens.js';
import { AskTessmaAi } from './ai/AskTessmaAi.js';
import {
  ExportHistoryScreen,
  GlobalSearchScreen,
  MetricCardsScreen,
  ReportBuilderScreen,
  ReportViewerScreen,
  ReportsListScreen,
  SavedSearchesScreen,
} from './analytics/screens.js';
import {
  ApprovalDetailScreen,
  ApprovalInboxScreen,
  MyTasksScreen,
  TaskDetailScreen,
} from './work/screens.js';
import { FinanceOverviewScreen } from './finance/overview-screen.js';
import {
  AgeingScreen,
  CreditNotesScreen,
  InvoiceDetailScreen,
  InvoiceEditorScreen,
  ReceiptsScreen,
} from './finance/receivables-screens.js';
import {
  BillsScreen as DesignerBillsScreen,
  CustomersScreen as DesignerCustomersScreen,
  InvoicesScreen as DesignerInvoicesScreen,
  SuppliersScreen as DesignerSuppliersScreen,
} from './finance/designer-screens.js';
import {
  BillDetailScreen,
  BillEntryScreen,
  PaymentsScreen,
  SupplierCreditsScreen,
} from './finance/payables-screens.js';
import {
  ChartOfAccountsScreen,
  FinancialCalendarScreen,
  GeneralLedgerScreen,
  JournalDetailScreen,
  JournalsScreen,
  TrialBalanceScreen,
} from './finance/ledger-screens.js';
import {
  CodingSetupScreen,
  EntitySetupScreen,
  FinanceReportsScreen,
  ItemDefaultsScreen,
  RecurringScreen,
  RevaluationScreen,
} from './finance/setup-screens.js';
import { MatchRulesScreen } from './finance/banking-screens.js';
import { BankAccountsScreen, BankReconciliationScreen } from './finance/banking-screens.js';
import { VatPeriodsScreen, VatReturnScreen } from './finance/vat-screens.js';
import { FinanceEntryCards } from './finance/entry-cards.js';
import {
  CustomerDetailScreen,
  DeliveryHistoryScreen,
  InvoiceDocumentScreen,
  SendInvoiceScreen,
} from './finance/document-screens.js';

/**
 * The application router — **TanStack Router**, per the approved Phase 2A
 * technology stack.
 *
 * Routes are defined in code rather than by file convention, so the route tree
 * and the guards on it are one reviewable object. The guards live in
 * `beforeLoad`, which runs before a route's component renders and before any of
 * its data loads — so a deep link, a pasted URL and a browser-history entry all
 * meet the same refusal, which is enforcement point 2 of §6.2.
 */

export interface RouterContext {
  readonly session: SessionState;
  readonly gateway: SessionGateway;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: () => <Outlet />,
  notFoundComponent: NotFoundPage,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginScreen,
});

const callbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/callback',
  component: CallbackScreen,
});

const logoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/logout',
  component: LogoutScreen,
});

/**
 * The authenticated boundary.
 *
 * Everything below it requires a session. `beforeLoad` redirects rather than
 * rendering an empty shell, and carries where the user was going so the
 * redirect back is to the page they asked for.
 */
const authenticatedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'authenticated',
  beforeLoad: ({ context, location }) => {
    if (!context.session.authenticated) {
      throw redirect({ to: '/login', search: { returnTo: location.href } });
    }
  },
  component: AuthenticatedLayout,
});

/**
 * SCREEN 00 — the Main Dashboard, and `/` is where it lives.
 *
 * This used to redirect to `/m/dashboard`, which put the product's landing page
 * behind the generic module route and gave it the module page's identity. The
 * Main Dashboard is not a module: it is the cross-module command centre, and
 * the one screen every signed-in person reaches whatever they hold.
 *
 * NO GUARD BEYOND BEING SIGNED IN, deliberately, and the endpoint agrees.
 * Somebody whose roles have not been assigned yet must still land somewhere
 * that explains itself — a 403 on the first screen after login is
 * indistinguishable from a broken product. Every section inside is composed by
 * the server against this caller's entitlements and permissions.
 */
const indexRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/',
  beforeLoad: ({ context }) => {
    requireAuthenticated(context.session, '/');
  },
  component: MainDashboardRoute,
  errorComponent: RouteError,
});

function MainDashboardRoute() {
  const router = useRouter();
  const session = router.options.context.session;
  return (
    <DashboardScreen
      gateway={dashboardGateway}
      // Recent activity is this person's own actions, so the avatar beside each
      // row is theirs. Taken from the session rather than from the rows: the
      // rows do not carry an actor because there is only ever one.
      initials={session.authenticated ? session.user.initials : ''}
      onOpen={(path) => {
        void router.navigate({ to: path });
      }}
    />
  );
}

/**
 * A module surface.
 *
 * The guard runs entitlement first and permission second, through the same
 * `authorise()` the service layer uses. An unentitled module answers not found,
 * exactly as the API does — a route that said "not entitled" would disclose
 * what the API is careful not to.
 */
const moduleRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/m/$moduleId',
  beforeLoad: ({ context, params }) => {
    const session = requireAuthenticated(context.session, `/m/${params.moduleId}`);
    const entry = FROZEN_NAVIGATION.find((module) => module.id === params.moduleId);
    if (entry === undefined) throw new RouteNotFoundError();

    const sku = skuForNavigation(entry.id);
    if (sku === undefined) {
      // Mandatory Platform Core navigation: Dashboard and Administration.
      // Administration still requires a permission; Dashboard requires none
      // beyond being signed in.
      if (entry.id === 'administration') {
        guardRoute(session, { permission: 'platform.tenant.read' });
      }
      return;
    }

    if (!reachableNavigationModules(session).has(entry.id)) throw new RouteNotFoundError();
  },
  component: ModuleScreen,
  errorComponent: RouteError,
});

/**
 * The Stage 5 work surfaces, deliberately OUTSIDE `/m/:moduleId`.
 *
 * My Tasks and the approval inbox are not a module. They are the platform's
 * own, and every module's work arrives in the same two places — which is the
 * point of building them once. Putting them under a module id would make them
 * look like one module's feature and would need a thirteenth navigation entry,
 * and the navigation is frozen at twelve.
 *
 * Each guard runs the SAME `authorise()` the API runs, so a deep link, a pasted
 * URL and a history entry all meet the refusal the server would give. The
 * server refuses again regardless: this is enforcement point 2, never the only
 * one.
 */
/**
 * PARTY MASTER IS PLATFORM CORE, so it sits here with `/tasks` rather than
 * under a module. D-2 assigns "who somebody is" to Core and Finance is only
 * its first consumer; a `/finance/parties` route would make CRM's version of
 * the same organisation a second record, which FR-PTY-001 forbids outright.
 */
const partiesRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/parties',
  beforeLoad: ({ context }) => {
    guardRoute(requireAuthenticated(context.session, '/parties'), {
      permission: 'platform.party.read',
    });
  },
  component: PartiesRoute,
  errorComponent: RouteError,
});

const catalogueRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/catalogue',
  beforeLoad: ({ context }) => {
    guardRoute(requireAuthenticated(context.session, '/catalogue'), {
      permission: 'platform.catalogue.read',
    });
  },
  component: CatalogueRoute,
  errorComponent: RouteError,
});

const tasksRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/tasks',
  beforeLoad: ({ context }) => {
    guardRoute(requireAuthenticated(context.session, '/tasks'), {
      permission: 'platform.task.read',
    });
  },
  component: MyTasksRoute,
  errorComponent: RouteError,
});

const taskRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/tasks/$taskId',
  beforeLoad: ({ context, params }) => {
    guardRoute(requireAuthenticated(context.session, `/tasks/${params.taskId}`), {
      permission: 'platform.task.read',
    });
  },
  component: TaskRoute,
  errorComponent: RouteError,
});

const approvalsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/approvals',
  beforeLoad: ({ context }) => {
    guardRoute(requireAuthenticated(context.session, '/approvals'), {
      permission: 'platform.approval.read',
    });
  },
  component: ApprovalInboxRoute,
  errorComponent: RouteError,
});

const approvalRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/approvals/$instanceId',
  beforeLoad: ({ context, params }) => {
    guardRoute(requireAuthenticated(context.session, `/approvals/${params.instanceId}`), {
      permission: 'platform.approval.read',
    });
  },
  component: ApprovalRoute,
  errorComponent: RouteError,
});

/**
 * The Stage 6 surfaces, outside `/m/:moduleId` for the Stage 5 reason.
 *
 * Search, reports, exports and metrics are the PLATFORM'S, not one module's:
 * every module's records arrive in the same search and the same report
 * builder, which is the point of building them once. The navigation is frozen
 * at twelve entries, so they are reached from the Dashboard's entry cards and
 * by direct URL — not by a thirteenth entry.
 *
 * Each guard names the SAME permission the controller names, so a deep link
 * meets the refusal the API would give. The API refuses again regardless, and
 * every row these screens show has been through `accessPredicate` in SQL:
 * this is enforcement point 2, never the only one.
 */
const searchRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/search',
  validateSearch: (search: Record<string, unknown>): { readonly q: string } => ({
    q: typeof search['q'] === 'string' ? search['q'] : '',
  }),
  beforeLoad: ({ context }) => {
    guardRoute(requireAuthenticated(context.session, '/search'), {
      permission: 'platform.search.query',
    });
  },
  component: SearchRoute,
  errorComponent: RouteError,
});

const savedSearchesRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/search/saved',
  beforeLoad: ({ context }) => {
    guardRoute(requireAuthenticated(context.session, '/search/saved'), {
      permission: 'platform.search.save',
    });
  },
  component: SavedSearchesRoute,
  errorComponent: RouteError,
});

const reportsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/reports',
  beforeLoad: ({ context }) => {
    guardRoute(requireAuthenticated(context.session, '/reports'), {
      permission: 'platform.report.read',
    });
  },
  component: ReportsRoute,
  errorComponent: RouteError,
});

const reportBuilderRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/reports/new',
  beforeLoad: ({ context }) => {
    guardRoute(requireAuthenticated(context.session, '/reports/new'), {
      permission: 'platform.report.manage',
    });
  },
  component: ReportBuilderRoute,
  errorComponent: RouteError,
});

const reportRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/reports/$reportKey',
  beforeLoad: ({ context, params }) => {
    guardRoute(requireAuthenticated(context.session, `/reports/${params.reportKey}`), {
      permission: 'platform.report.run',
    });
  },
  component: ReportRoute,
  errorComponent: RouteError,
});

const exportsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/exports',
  beforeLoad: ({ context }) => {
    guardRoute(requireAuthenticated(context.session, '/exports'), {
      permission: 'platform.export.read',
    });
  },
  component: ExportsRoute,
  errorComponent: RouteError,
});

const metricsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/metrics',
  beforeLoad: ({ context }) => {
    guardRoute(requireAuthenticated(context.session, '/metrics'), {
      permission: 'platform.metric.read',
    });
  },
  component: MetricsRoute,
  errorComponent: RouteError,
});

/**
 * THE STAGE 8 FINANCE SURFACES.
 *
 * Under `/finance`, outside `/m/:moduleId`, for the Stage 5 and Stage 6
 * reason: the frozen navigation has twelve entries and the Finance entry
 * carries one landing page. These are the pages that landing page leads to,
 * reached by link and by direct URL.
 *
 * EVERY GUARD NAMES THE SAME PERMISSION THE CONTROLLER NAMES, so a deep link
 * meets the refusal the API would give. The API refuses again regardless, and
 * every row these screens show has been through `accessPredicate` in SQL: this
 * is enforcement point 2, never the only one. A guard that passed would still
 * produce an empty screen if the server disagreed.
 *
 * `finance.invoice.read` is the entry permission for the module's landing
 * page rather than something broader, because a person who may read invoices
 * and nothing else should still have somewhere to land.
 */
const financeRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/finance',
  beforeLoad: ({ context }) => {
    guardRoute(requireAuthenticated(context.session, '/finance'), {
      permission: 'finance.invoice.read',
      moduleKey: 'finance',
    });
  },
  component: FinanceOverviewRoute,
  errorComponent: RouteError,
});

/**
 * EACH ROUTE IS DECLARED WITH ITS LITERAL PATH rather than built by a helper,
 * and the repetition buys something real: TanStack derives the router's
 * `to` union from those literals, so a typo in a `navigate` call is a
 * compile error. A `financeChild(path, …)` factory was tried first and erased
 * every literal to `string`, which silently removed that check from the whole
 * Finance surface.
 */
/**
 * CUSTOMERS AND SUPPLIERS ARE FINANCE PAGES OVER PARTY MASTER.
 *
 * They guard on `finance.customer.read` / `finance.supplier.read` rather than
 * on a Party permission, because what they show is the Finance relationship —
 * the terms, the credit standing, the coding — and the server's list endpoint
 * checks exactly that permission. The party's own record stays where it was,
 * under Administration, for the people who maintain identity rather than
 * ledgers.
 */
const financeCustomersRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/finance/customers',
  beforeLoad: ({ context }) => {
    guardRoute(requireAuthenticated(context.session, '/finance/customers'), {
      permission: 'finance.customer.read',
      moduleKey: 'finance',
    });
  },
  component: CustomersRoute,
  errorComponent: RouteError,
});

const financeSuppliersRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/finance/suppliers',
  beforeLoad: ({ context }) => {
    guardRoute(requireAuthenticated(context.session, '/finance/suppliers'), {
      permission: 'finance.supplier.read',
      moduleKey: 'finance',
    });
  },
  component: SuppliersRoute,
  errorComponent: RouteError,
});

const financeInvoicesRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/finance/invoices',
  beforeLoad: ({ context }) => {
    guardRoute(requireAuthenticated(context.session, '/finance/invoices'), {
      permission: 'finance.invoice.read',
      moduleKey: 'finance',
    });
  },
  component: InvoicesRoute,
  errorComponent: RouteError,
});

const financeInvoiceNewRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/finance/invoices/new',
  beforeLoad: ({ context }) => {
    guardRoute(requireAuthenticated(context.session, '/finance/invoices/new'), {
      permission: 'finance.invoice.create',
      moduleKey: 'finance',
    });
  },
  component: NewInvoiceRoute,
  errorComponent: RouteError,
});

const financeCreditNotesRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/finance/credit-notes',
  beforeLoad: ({ context }) => {
    guardRoute(requireAuthenticated(context.session, '/finance/credit-notes'), {
      permission: 'finance.invoice.read',
      moduleKey: 'finance',
    });
  },
  component: CreditNotesRoute,
  errorComponent: RouteError,
});

const financeReceiptsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/finance/receipts',
  beforeLoad: ({ context }) => {
    guardRoute(requireAuthenticated(context.session, '/finance/receipts'), {
      permission: 'finance.invoice.read',
      moduleKey: 'finance',
    });
  },
  component: ReceiptsRoute,
  errorComponent: RouteError,
});

const financeReceivablesAgeingRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/finance/receivables',
  beforeLoad: ({ context }) => {
    guardRoute(requireAuthenticated(context.session, '/finance/receivables'), {
      permission: 'finance.invoice.read',
      moduleKey: 'finance',
    });
  },
  component: ReceivablesAgeingRoute,
  errorComponent: RouteError,
});

const financeBillsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/finance/bills',
  beforeLoad: ({ context }) => {
    guardRoute(requireAuthenticated(context.session, '/finance/bills'), {
      permission: 'finance.bill.read',
      moduleKey: 'finance',
    });
  },
  component: BillsRoute,
  errorComponent: RouteError,
});

const financeBillNewRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/finance/bills/new',
  beforeLoad: ({ context }) => {
    guardRoute(requireAuthenticated(context.session, '/finance/bills/new'), {
      permission: 'finance.bill.create',
      moduleKey: 'finance',
    });
  },
  component: NewBillRoute,
  errorComponent: RouteError,
});

const financeSupplierCreditsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/finance/supplier-credits',
  beforeLoad: ({ context }) => {
    guardRoute(requireAuthenticated(context.session, '/finance/supplier-credits'), {
      permission: 'finance.supplier_credit.read',
      moduleKey: 'finance',
    });
  },
  component: SupplierCreditsRoute,
  errorComponent: RouteError,
});

const financePaymentsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/finance/payments',
  beforeLoad: ({ context }) => {
    guardRoute(requireAuthenticated(context.session, '/finance/payments'), {
      permission: 'finance.bill.read',
      moduleKey: 'finance',
    });
  },
  component: PaymentsRoute,
  errorComponent: RouteError,
});

const financePayablesAgeingRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/finance/payables',
  beforeLoad: ({ context }) => {
    guardRoute(requireAuthenticated(context.session, '/finance/payables'), {
      permission: 'finance.bill.read',
      moduleKey: 'finance',
    });
  },
  component: PayablesAgeingRoute,
  errorComponent: RouteError,
});

const financeAccountsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/finance/accounts',
  beforeLoad: ({ context }) => {
    guardRoute(requireAuthenticated(context.session, '/finance/accounts'), {
      permission: 'finance.account.read',
      moduleKey: 'finance',
    });
  },
  component: AccountsRoute,
  errorComponent: RouteError,
});

/**
 * STAGE 8 GAP CLOSURE — five surfaces for capabilities that had no screen.
 *
 * Each guards on the permission its own write needs, which is a COURTESY and
 * not the control: the server answers 403 to the same request made by hand,
 * and every one of these screens renders that refusal in the server's words
 * rather than its own.
 */
const financeSetupRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/finance/setup',
  beforeLoad: ({ context }) => {
    guardRoute(requireAuthenticated(context.session, '/finance/setup'), {
      permission: 'finance.account.read',
      moduleKey: 'finance',
    });
  },
  component: FinanceSetupRoute,
  errorComponent: RouteError,
});

const financeItemDefaultsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/finance/item-defaults',
  beforeLoad: ({ context }) => {
    guardRoute(requireAuthenticated(context.session, '/finance/item-defaults'), {
      permission: 'platform.catalogue.read',
      moduleKey: 'finance',
    });
  },
  component: FinanceItemDefaultsRoute,
  errorComponent: RouteError,
});

/**
 * FR-LED-006 and FR-SET-006. Reading the setup needs the same permission as
 * reading a chart of accounts, because that is what it is: how a posting may
 * be coded, and what a due date is derived from. Changing it needs
 * `finance.setup.manage`, which the screen asks for before offering a form —
 * and the server asks for again, which is the control.
 */
const financeCodingRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/finance/coding',
  beforeLoad: ({ context }) => {
    guardRoute(requireAuthenticated(context.session, '/finance/coding'), {
      permission: 'finance.account.read',
      moduleKey: 'finance',
    });
  },
  component: FinanceCodingRoute,
  errorComponent: RouteError,
});

const financeRevaluationRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/finance/revaluations',
  beforeLoad: ({ context }) => {
    guardRoute(requireAuthenticated(context.session, '/finance/revaluations'), {
      permission: 'finance.revaluation.read',
      moduleKey: 'finance',
    });
  },
  component: FinanceRevaluationRoute,
  errorComponent: RouteError,
});

const financeRecurringRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/finance/recurring',
  beforeLoad: ({ context }) => {
    guardRoute(requireAuthenticated(context.session, '/finance/recurring'), {
      permission: 'finance.recurring.read',
      moduleKey: 'finance',
    });
  },
  component: FinanceRecurringRoute,
  errorComponent: RouteError,
});

const financeMatchRulesRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/finance/match-rules',
  beforeLoad: ({ context }) => {
    guardRoute(requireAuthenticated(context.session, '/finance/match-rules'), {
      permission: 'finance.match_rule.read',
      moduleKey: 'finance',
    });
  },
  component: FinanceMatchRulesRoute,
  errorComponent: RouteError,
});

const financeStandardReportsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/finance/reports',
  beforeLoad: ({ context }) => {
    guardRoute(requireAuthenticated(context.session, '/finance/reports'), {
      permission: 'platform.report.read',
      moduleKey: 'finance',
    });
  },
  component: FinanceStandardReportsRoute,
  errorComponent: RouteError,
});

const financeCalendarRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/finance/calendar',
  beforeLoad: ({ context }) => {
    guardRoute(requireAuthenticated(context.session, '/finance/calendar'), {
      permission: 'finance.account.read',
      moduleKey: 'finance',
    });
  },
  component: CalendarRoute,
  errorComponent: RouteError,
});

const financeJournalsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/finance/journals',
  beforeLoad: ({ context }) => {
    guardRoute(requireAuthenticated(context.session, '/finance/journals'), {
      permission: 'finance.journal.read',
      moduleKey: 'finance',
    });
  },
  component: JournalsRoute,
  errorComponent: RouteError,
});

const financeLedgerRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/finance/ledger',
  beforeLoad: ({ context }) => {
    guardRoute(requireAuthenticated(context.session, '/finance/ledger'), {
      permission: 'finance.journal.read',
      moduleKey: 'finance',
    });
  },
  component: GeneralLedgerRoute,
  errorComponent: RouteError,
});

const financeTrialBalanceRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/finance/trial-balance',
  beforeLoad: ({ context }) => {
    guardRoute(requireAuthenticated(context.session, '/finance/trial-balance'), {
      permission: 'finance.report.read',
      moduleKey: 'finance',
    });
  },
  component: TrialBalanceRoute,
  errorComponent: RouteError,
});

const financeBankAccountsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/finance/bank-accounts',
  beforeLoad: ({ context }) => {
    guardRoute(requireAuthenticated(context.session, '/finance/bank-accounts'), {
      permission: 'finance.bank.read',
      moduleKey: 'finance',
    });
  },
  component: BankAccountsRoute,
  errorComponent: RouteError,
});

const financeVatRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/finance/vat',
  beforeLoad: ({ context }) => {
    guardRoute(requireAuthenticated(context.session, '/finance/vat'), {
      permission: 'finance.vat.read',
      moduleKey: 'finance',
    });
  },
  component: VatPeriodsRoute,
  errorComponent: RouteError,
});

const financeInvoiceRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/finance/invoices/$invoiceId',
  beforeLoad: ({ context, params }) => {
    guardRoute(requireAuthenticated(context.session, `/finance/invoices/${params.invoiceId}`), {
      permission: 'finance.invoice.read',
      moduleKey: 'finance',
    });
  },
  component: InvoiceRoute,
  errorComponent: RouteError,
});

/**
 * THE FOUR ROUTES THE SALES-INVOICE SLICE ADDS.
 *
 * EACH GUARDS ON THE PERMISSION ITS OWN API REQUIRES, not on the module's entry
 * permission. Reading a document is a read of the invoice; sending one is
 * `finance.invoice.send`, which is a permission of its own because it is the only
 * act in Finance that leaves the building. The guard is still a courtesy: the
 * server refuses the same request made by hand.
 */
const financeCustomerRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/finance/customers/$partyId',
  beforeLoad: ({ context, params }) => {
    guardRoute(requireAuthenticated(context.session, `/finance/customers/${params.partyId}`), {
      permission: 'finance.customer.read',
      moduleKey: 'finance',
    });
  },
  component: CustomerRoute,
  errorComponent: RouteError,
});

const financeInvoiceDocumentRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/finance/invoices/$invoiceId/document',
  beforeLoad: ({ context, params }) => {
    guardRoute(
      requireAuthenticated(context.session, `/finance/invoices/${params.invoiceId}/document`),
      { permission: 'finance.invoice.read', moduleKey: 'finance' },
    );
  },
  component: InvoiceDocumentRoute,
  errorComponent: RouteError,
});

const financeInvoiceSendRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/finance/invoices/$invoiceId/send',
  beforeLoad: ({ context, params }) => {
    guardRoute(
      requireAuthenticated(context.session, `/finance/invoices/${params.invoiceId}/send`),
      {
        permission: 'finance.invoice.send',
        moduleKey: 'finance',
      },
    );
  },
  component: SendInvoiceRoute,
  errorComponent: RouteError,
});

const financeDeliveriesRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/finance/deliveries',
  beforeLoad: ({ context }) => {
    guardRoute(requireAuthenticated(context.session, '/finance/deliveries'), {
      permission: 'finance.invoice.read',
      moduleKey: 'finance',
    });
  },
  component: DeliveryHistoryRoute,
  errorComponent: RouteError,
});

const financeBillRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/finance/bills/$billId',
  beforeLoad: ({ context, params }) => {
    guardRoute(requireAuthenticated(context.session, `/finance/bills/${params.billId}`), {
      permission: 'finance.bill.read',
      moduleKey: 'finance',
    });
  },
  component: BillRoute,
  errorComponent: RouteError,
});

const financeJournalRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/finance/journals/$journalId',
  beforeLoad: ({ context, params }) => {
    guardRoute(requireAuthenticated(context.session, `/finance/journals/${params.journalId}`), {
      permission: 'finance.journal.read',
      moduleKey: 'finance',
    });
  },
  component: JournalRoute,
  errorComponent: RouteError,
});

const financeReconciliationRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/finance/bank-accounts/$bankAccountId',
  beforeLoad: ({ context, params }) => {
    guardRoute(
      requireAuthenticated(context.session, `/finance/bank-accounts/${params.bankAccountId}`),
      {
        permission: 'finance.bank.read',
        moduleKey: 'finance',
      },
    );
  },
  component: ReconciliationRoute,
  errorComponent: RouteError,
});

const financeVatReturnRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/finance/vat/$vatReturnId',
  beforeLoad: ({ context, params }) => {
    guardRoute(requireAuthenticated(context.session, `/finance/vat/${params.vatReturnId}`), {
      permission: 'finance.vat.read',
      moduleKey: 'finance',
    });
  },
  component: VatReturnRoute,
  errorComponent: RouteError,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  callbackRoute,
  logoutRoute,
  authenticatedRoute.addChildren([
    indexRoute,
    moduleRoute,
    partiesRoute,
    catalogueRoute,
    tasksRoute,
    taskRoute,
    approvalsRoute,
    approvalRoute,
    searchRoute,
    savedSearchesRoute,
    reportsRoute,
    reportBuilderRoute,
    reportRoute,
    exportsRoute,
    metricsRoute,
    financeRoute,
    financeCustomersRoute,
    financeSuppliersRoute,
    financeInvoicesRoute,
    financeInvoiceNewRoute,
    financeInvoiceRoute,
    financeCustomerRoute,
    financeInvoiceDocumentRoute,
    financeInvoiceSendRoute,
    financeDeliveriesRoute,
    financeCreditNotesRoute,
    financeReceiptsRoute,
    financeReceivablesAgeingRoute,
    financeBillsRoute,
    financeBillNewRoute,
    financeBillRoute,
    financeSupplierCreditsRoute,
    financePaymentsRoute,
    financePayablesAgeingRoute,
    financeAccountsRoute,
    financeCalendarRoute,
    financeSetupRoute,
    financeItemDefaultsRoute,
    financeCodingRoute,
    financeRevaluationRoute,
    financeRecurringRoute,
    financeMatchRulesRoute,
    financeStandardReportsRoute,
    financeJournalsRoute,
    financeJournalRoute,
    financeLedgerRoute,
    financeTrialBalanceRoute,
    financeBankAccountsRoute,
    financeReconciliationRoute,
    financeVatRoute,
    financeVatReturnRoute,
  ]),
]);

/** One gateway of each kind for the application, not one per render. */
const workGateway = createWorkGateway();
const analyticsGateway = createAnalyticsGateway();
const dashboardGateway = createDashboardGateway();
const aiGateway = createAiGateway();
const financeGateway = createFinanceGateway();
const partiesGateway = createPartiesGateway();

/**
 * `history` is a TEST SEAM and nothing else.
 *
 * The application passes none and gets the browser's own history, which is the
 * only correct source in a browser. A test passes a memory history so the
 * guards can be entered at a URL — which is the case they exist for: a deep
 * link, a pasted address and a history entry all arrive this way, and a guard
 * only reachable by clicking is a guard that has never met the thing it is
 * defending against.
 */
export function createApplicationRouter(context: RouterContext, history?: RouterHistory) {
  return createRouter({
    routeTree,
    context,
    defaultNotFoundComponent: NotFoundPage,
    ...(history === undefined ? {} : { history }),
  });
}

export type ApplicationRouter = ReturnType<typeof createApplicationRouter>;

declare module '@tanstack/react-router' {
  interface Register {
    router: ApplicationRouter;
  }
}

// ---- screens ---------------------------------------------------------------

function AuthenticatedLayout() {
  const router = useRouter();
  /**
   * THE DRAWER LIVES IN THE LAYOUT, NOT IN A ROUTE, and that is what §14 means
   * by "the single global entry point". A route would give it a URL, a back
   * button and a page of its own; the frozen design makes it an overlay over
   * whatever you were already looking at, so the question keeps its context.
   */
  const [askOpen, setAskOpen] = useState(false);
  /**
   * **SUBSCRIBED TO THE LOCATION, NOT A SNAPSHOT OF IT.**
   *
   * This read `router.state.location.pathname` off `useRouter()`, and
   * `useRouter()` hands back the router INSTANCE without subscribing the
   * component to its state. So the whole chrome — breadcrumb, `<h1>`,
   * description, and the sidebar's own active module and page — was computed
   * once, when the layout mounted, and never again. `<Outlet/>` re-rendered on
   * every navigation because the matched route changed; the frame around it
   * did not.
   *
   * WHAT THAT LOOKED LIKE. Sign in, land on `/`, click Finance: the Finance
   * page rendered under a header still reading "Dashboard › Overview /
   * Dashboard Overview / A consolidated view of your business performance
   * across all modules." Entering `/finance` at the address bar was always
   * correct, because that mounts the layout there — which is why every route
   * test, every acceptance step and every screen capture passed. They all
   * enter at the address; nobody clicked.
   *
   * `useRouterState` subscribes, and the selector keeps the re-render to the
   * one thing this layout reads.
   */
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const context = router.options.context;
  const session = context.session;
  if (!session.authenticated) return null;

  /*
   * WHERE WE ARE, READ FROM THE URL RATHER THAN ASSUMED.
   *
   * This passed `LANDING_PAGE` — the literal 'Overview' — for every route in
   * the product, so /finance/invoices announced itself as "Finance › Overview"
   * with a page title of "Overview". One table in `navigation.ts` now answers
   * it, and the sidebar's sub-navigation is built from the same table, so the
   * breadcrumb and the sidebar cannot disagree about what page this is.
   */
  const { moduleId, page, description, title } = pageIdentityFor(pathname);
  const entry = FROZEN_NAVIGATION.find((module) => module.id === moduleId);

  return (
    <ShellBridgeProvider openAskAi={() => setAskOpen(true)}>
      <AuthenticatedChrome
        moduleId={moduleId}
        page={page}
        {...(description === undefined ? {} : { description })}
        {...(title === undefined ? {} : { title })}
        entryExists={entry !== undefined}
        session={session}
        askOpen={askOpen}
        onCloseAsk={() => setAskOpen(false)}
        onNavigate={(nextModuleId, nextPage) => {
          void router.navigate({ to: pathFor(nextModuleId, nextPage) });
        }}
        onSearch={(query) => {
          void router.navigate({ to: '/search', search: { q: query } });
        }}
        onOpenPath={(path) => {
          void router.navigate({ to: path });
        }}
      />
    </ShellBridgeProvider>
  );
}

/**
 * The chrome itself, split out for ONE reason: it has to be INSIDE the bridge
 * provider to read the unread count a screen reported, and a component cannot
 * consume a context it is itself providing.
 */
function AuthenticatedChrome({
  moduleId,
  page,
  description,
  title,
  entryExists,
  session,
  askOpen,
  onCloseAsk,
  onNavigate,
  onSearch,
  onOpenPath,
}: {
  readonly moduleId: string;
  readonly page: string;
  readonly description?: string;
  readonly title?: string;
  readonly entryExists: boolean;
  readonly session: Extract<SessionState, { authenticated: true }>;
  readonly askOpen: boolean;
  readonly onCloseAsk: () => void;
  readonly onNavigate: (moduleId: string, page: string) => void;
  readonly onSearch: (query: string) => void;
  readonly onOpenPath: (path: string) => void;
}) {
  const bridge = useShellBridge();
  return (
    <ApplicationShell
      session={session}
      activeModuleId={moduleId}
      activePage={page}
      {...(bridge.unread === undefined ? {} : { notificationCount: bridge.unread })}
      onAskAi={bridge.openAskAi}
      onNavigate={onNavigate}
      onSearch={onSearch}
      onOpenPath={onOpenPath}
      {...(description === undefined ? {} : { pageDescription: description })}
      {...(title === undefined ? {} : { pageTitle: title })}
    >
      <Outlet />
      {!entryExists && <NotFoundPage />}
      <AskTessmaAi open={askOpen} onClose={onCloseAsk} gateway={aiGateway} />
    </ApplicationShell>
  );
}

function ModuleScreen() {
  const { moduleId } = moduleRoute.useParams();
  const entry = FROZEN_NAVIGATION.find((module) => module.id === moduleId);

  /*
   * ONE MAIN DASHBOARD, AT TWO ADDRESSES — not two different pages.
   *
   * `/` is where the sidebar points and where Screen 00 lives. `/m/dashboard`
   * stays addressable because links and history entries exist, and it renders
   * the same screen. What it must not do is render a SECOND landing page: that
   * is the defect PR #17 removed from Finance, where `/finance` and
   * `/m/finance` showed different things and only one of them was reachable
   * from the navigation.
   */
  if (moduleId === 'dashboard') return <MainDashboardRoute />;
  /**
   * FINANCE LANDS ON ITS OWN OVERVIEW, not on the generic placeholder. The
   * navigation is still frozen at twelve entries and Finance still has one
   * landing page — this is what that page contains now that the module
   * exists.
   */
  if (moduleId === 'finance') {
    // The same page as `/finance`, which is where the sidebar points.
    return <FinanceOverviewRoute />;
  }

  return <ModuleLanding label={entry?.label ?? moduleId} />;
}

function usePermissions(): readonly string[] {
  const session = useRouter().options.context.session;
  return session.authenticated ? session.permissions : [];
}

function MyTasksRoute() {
  const router = useRouter();
  return (
    <MyTasksScreen
      gateway={workGateway}
      onOpen={(taskId) => {
        void router.navigate({ to: '/tasks/$taskId', params: { taskId } });
      }}
    />
  );
}

function TaskRoute() {
  const { taskId } = taskRoute.useParams();
  const permissions = usePermissions();
  return (
    <TaskDetailScreen
      gateway={workGateway}
      taskId={taskId}
      canComplete={permissions.includes('platform.task.complete')}
      canComment={permissions.includes('platform.comment.create')}
    />
  );
}

function ApprovalInboxRoute() {
  const router = useRouter();
  return (
    <ApprovalInboxScreen
      gateway={workGateway}
      onOpen={(instanceId) => {
        void router.navigate({ to: '/approvals/$instanceId', params: { instanceId } });
      }}
    />
  );
}

function ApprovalRoute() {
  const { instanceId } = approvalRoute.useParams();
  const permissions = usePermissions();
  return (
    <ApprovalDetailScreen
      gateway={workGateway}
      instanceId={instanceId}
      canDecide={permissions.includes('platform.approval.act')}
      canComment={permissions.includes('platform.comment.create')}
    />
  );
}

function SearchRoute() {
  const { q } = searchRoute.useSearch();
  return <GlobalSearchScreen gateway={analyticsGateway} initialTerm={q} />;
}

function SavedSearchesRoute() {
  const router = useRouter();
  return (
    <SavedSearchesScreen
      gateway={analyticsGateway}
      onRun={(term) => {
        void router.navigate({ to: '/search', search: { q: term } });
      }}
    />
  );
}

function ReportsRoute() {
  const router = useRouter();
  const permissions = usePermissions();
  return (
    <ReportsListScreen
      gateway={analyticsGateway}
      canManage={permissions.includes('platform.report.manage')}
      onOpen={(reportKey) => {
        void router.navigate({ to: '/reports/$reportKey', params: { reportKey } });
      }}
      onNew={() => {
        void router.navigate({ to: '/reports/new' });
      }}
    />
  );
}

function ReportBuilderRoute() {
  const router = useRouter();
  return (
    <ReportBuilderScreen
      gateway={analyticsGateway}
      onCreated={(reportKey) => {
        void router.navigate({ to: '/reports/$reportKey', params: { reportKey } });
      }}
      onCancel={() => {
        void router.navigate({ to: '/reports' });
      }}
    />
  );
}

function ReportRoute() {
  const { reportKey } = reportRoute.useParams();
  const permissions = usePermissions();
  return (
    <ReportViewerScreen
      gateway={analyticsGateway}
      reportKey={reportKey}
      canExport={permissions.includes('platform.export.create')}
    />
  );
}

function ExportsRoute() {
  return <ExportHistoryScreen gateway={analyticsGateway} />;
}

function MetricsRoute() {
  return <MetricCardsScreen gateway={analyticsGateway} />;
}

function RouteError({ error }: { readonly error: unknown }) {
  if (error instanceof RouteForbiddenError) return <ForbiddenPage />;
  if (error instanceof RouteNotFoundError) return <NotFoundPage />;
  if (error instanceof NotAuthenticatedError) return <NotFoundPage />;
  throw error;
}

function LoginScreen() {
  const router = useRouter();
  const gateway = router.options.context.gateway;
  const [pending, setPending] = useState(false);

  const signIn = useCallback(() => {
    setPending(true);
    void gateway.beginSignIn(globalThis.location.pathname).catch(() => {
      setPending(false);
    });
  }, [gateway]);

  return <SignInPage onSignIn={signIn} pending={pending} />;
}

function CallbackScreen() {
  /**
   * Reachable only if somebody opens this path directly.
   *
   * The authorisation code is exchanged SERVER-SIDE — the provider redirects to
   * the API, which sets the session cookie and sends the browser on — so this
   * screen has nothing to complete and deliberately does not try. Keeping it as
   * a signpost rather than deleting the route means a stale bookmark lands on
   * "sign in" instead of a 404.
   */
  return <SignInPage onSignIn={() => globalThis.location.assign('/login')} pending={false} />;
}

function LogoutScreen() {
  const router = useRouter();
  useEffect(() => {
    void router.options.context.gateway
      .signOut()
      .then((result) => {
        // Ending the TESSMA session without ending the provider's would mean
        // the next sign-in succeeds silently with no credentials, and the user
        // believes they logged out.
        globalThis.location.assign(result.endSessionUrl ?? '/login');
      })
      .catch(() => {
        globalThis.location.assign('/login');
      });
  }, [router]);
  return null;
}

// ---- Finance screens --------------------------------------------------------

/**
 * ONE LEGAL ENTITY AT A TIME, CHOSEN BY THE SERVER'S ANSWER RATHER THAN BY
 * THIS COMPONENT.
 *
 * Most Finance screens are scoped to a legal entity, and the browser does not
 * know which entities a caller reaches — that is the server's to say. So the
 * entity comes from the first account the accounts endpoint returns, which is
 * already filtered by the caller's own predicate: a person who reaches one
 * entity gets that one, and a person who reaches several gets the first of
 * theirs. Hard-coding a list here would be the browser holding an opinion
 * about reach, which is the one thing it must never do.
 */
function useFirstReachableEntity(): string | undefined {
  const { state } = useAsync(() => financeGateway.accounts(), []);
  return state.status === 'ready' ? state.value[0]?.legalEntityId : undefined;
}

/**
 * THE FINANCE LANDING PAGE, WHICH IS ONE PAGE AGAIN.
 *
 * `/finance` rendered the figures and `/m/finance` rendered the figures with
 * the module's links above them — and `/finance` is where the sidebar and the
 * breadcrumb both point, so the links were on a page nothing led to. Both
 * addresses now render this, in the order somebody reads it: what these books
 * are, what the figures say, what to start, where to go.
 */
function FinanceOverviewRoute() {
  const router = useRouter();
  const permissions = usePermissions();
  const entityId = useFirstReachableEntity();

  /**
   * QUICK CREATE — the two things this page exists to start.
   *
   * Offered on the same permissions the create routes guard on, and for the
   * same reason as every other hidden action here: it is a COURTESY. The guard
   * refuses the URL typed by hand and the server refuses the write again.
   */
  const actions = (
    <>
      {permissions.includes('finance.invoice.create') && (
        <Button
          variant="primary"
          onClick={() => {
            void router.navigate({ to: '/finance/invoices/new' });
          }}
        >
          New invoice
        </Button>
      )}
      {permissions.includes('finance.bill.create') && (
        <Button
          variant="secondary"
          onClick={() => {
            void router.navigate({ to: '/finance/bills/new' });
          }}
        >
          Enter a bill
        </Button>
      )}
    </>
  );

  return (
    <FinanceOverviewScreen
      finance={financeGateway}
      analytics={analyticsGateway}
      parties={partiesGateway}
      {...(entityId === undefined ? {} : { legalEntityId: entityId })}
      actions={actions}
      navigation={
        <FinanceEntryCards
          permissions={permissions}
          onOpen={(to) => {
            void router.navigate({ to });
          }}
        />
      }
      {...(permissions.includes('finance.invoice.read')
        ? {
            onViewInvoices: () => {
              void router.navigate({ to: '/finance/invoices' });
            },
          }
        : {})}
      {...(permissions.includes('finance.bill.read')
        ? {
            onViewBills: () => {
              void router.navigate({ to: '/finance/bills' });
            },
          }
        : {})}
    />
  );
}

function CustomersRoute() {
  const router = useRouter();
  const permissions = usePermissions();
  const entityId = useFirstReachableEntity();
  return (
    <DesignerCustomersScreen
      /*
       * BOTH PERMISSIONS, BECAUSE ADDING A CUSTOMER IS TWO WRITES — the same
       * pair `NewInvoiceRoute` requires for the same sheet. One of the two is a
       * form that fails halfway through.
       */
      canCreate={
        permissions.includes('platform.party.manage') &&
        permissions.includes('finance.customer.manage')
      }
      gateway={financeGateway}
      parties={partiesGateway}
      {...(entityId === undefined ? {} : { legalEntityId: entityId })}
      onOpen={(partyId) => {
        void router.navigate({ to: '/finance/customers/$partyId', params: { partyId } });
      }}
    />
  );
}

function SuppliersRoute() {
  const permissions = usePermissions();
  const entityId = useFirstReachableEntity();
  return (
    <DesignerSuppliersScreen
      gateway={financeGateway}
      parties={partiesGateway}
      /* BOTH PERMISSIONS, because the sheet registers the party and then
         relates it to this entity. */
      canCreate={
        permissions.includes('platform.party.manage') &&
        permissions.includes('finance.supplier.manage')
      }
      {...(entityId === undefined ? {} : { legalEntityId: entityId })}
    />
  );
}

function InvoicesRoute() {
  const router = useRouter();
  const permissions = usePermissions();
  return (
    <DesignerInvoicesScreen
      gateway={financeGateway}
      canCreate={permissions.includes('finance.invoice.create')}
      canSend={permissions.includes('finance.invoice.send')}
      onOpen={(invoiceId) => {
        void router.navigate({ to: '/finance/invoices/$invoiceId', params: { invoiceId } });
      }}
      onNew={() => {
        void router.navigate({ to: '/finance/invoices/new' });
      }}
      /*
       * THE DESIGNER'S ROW ACTIONS, AND ONLY THE TWO THAT LEAD SOMEWHERE. They
       * draw View PDF, Send PDF and an overflow menu on every row; the document
       * and the send are real routes, and there is nothing behind the menu.
       */
      onViewDocument={(invoiceId) => {
        void router.navigate({
          to: '/finance/invoices/$invoiceId/document',
          params: { invoiceId },
        });
      }}
      onSend={(invoiceId) => {
        void router.navigate({ to: '/finance/invoices/$invoiceId/send', params: { invoiceId } });
      }}
    />
  );
}

function NewInvoiceRoute() {
  const router = useRouter();
  const permissions = usePermissions();
  const entityId = useFirstReachableEntity();
  if (entityId === undefined) return <LoadingState title="Loading…" />;
  return (
    <InvoiceEditorScreen
      gateway={financeGateway}
      parties={partiesGateway}
      legalEntityId={entityId}
      /*
       * BOTH PERMISSIONS, BECAUSE ADDING A CUSTOMER IS TWO WRITES: the party
       * goes into shared records and the Finance relationship goes onto this
       * entity. Offering the button to somebody holding one of the two would
       * be offering a form that fails halfway through. Still only a courtesy —
       * the server refuses each write on its own.
       */
      canAddCustomer={
        permissions.includes('platform.party.manage') &&
        permissions.includes('finance.customer.manage')
      }
      canIssue={permissions.includes('finance.invoice.issue')}
      onCreated={(invoice) => {
        void router.navigate({
          to: '/finance/invoices/$invoiceId',
          params: { invoiceId: invoice.id },
        });
      }}
      onCancel={() => {
        void router.navigate({ to: '/finance/invoices' });
      }}
      onEditCustomer={(partyId) => {
        void router.navigate({ to: '/finance/customers/$partyId', params: { partyId } });
      }}
      onViewDocument={(invoiceId) => {
        void router.navigate({
          to: '/finance/invoices/$invoiceId/document',
          params: { invoiceId },
        });
      }}
    />
  );
}

function NewBillRoute() {
  const router = useRouter();
  const permissions = usePermissions();
  const entityId = useFirstReachableEntity();
  if (entityId === undefined) return <LoadingState title="Loading…" />;
  return (
    <BillEntryScreen
      gateway={financeGateway}
      parties={partiesGateway}
      legalEntityId={entityId}
      canAddSupplier={
        permissions.includes('platform.party.manage') &&
        permissions.includes('finance.supplier.manage')
      }
      onEntered={(bill) => {
        void router.navigate({ to: '/finance/bills/$billId', params: { billId: bill.id } });
      }}
    />
  );
}

function InvoiceRoute() {
  const router = useRouter();
  const { invoiceId } = financeInvoiceRoute.useParams();
  const permissions = usePermissions();
  return (
    <InvoiceDetailScreen
      gateway={financeGateway}
      invoiceId={invoiceId}
      canIssue={permissions.includes('finance.invoice.issue')}
      canEdit={permissions.includes('finance.invoice.create')}
      canDispute={permissions.includes('finance.invoice.dispute')}
      canSend={permissions.includes('finance.invoice.send')}
      onViewDocument={() => {
        void router.navigate({
          to: '/finance/invoices/$invoiceId/document',
          params: { invoiceId },
        });
      }}
      onSend={() => {
        void router.navigate({ to: '/finance/invoices/$invoiceId/send', params: { invoiceId } });
      }}
    />
  );
}

/**
 * Screen 03. The customer, their four figures and their invoices.
 *
 * `canManageParty` IS `platform.party.manage`, NOT A FINANCE PERMISSION. An
 * address belongs to the shared party master (D-2): correcting it here corrects
 * it for every module, so it takes the permission that governs identity.
 */
function CustomerRoute() {
  const router = useRouter();
  const permissions = usePermissions();
  const { partyId } = financeCustomerRoute.useParams();
  const entityId = useFirstReachableEntity();
  if (entityId === undefined) return <LoadingState title="Loading…" />;
  return (
    <CustomerDetailScreen
      gateway={financeGateway}
      partyId={partyId}
      legalEntityId={entityId}
      canManageParty={permissions.includes('platform.party.manage')}
      onOpenInvoice={(invoiceId) => {
        void router.navigate({ to: '/finance/invoices/$invoiceId', params: { invoiceId } });
      }}
      onNewInvoice={() => {
        void router.navigate({ to: '/finance/invoices/new' });
      }}
    />
  );
}

/** Screen 31. */
function InvoiceDocumentRoute() {
  const router = useRouter();
  const permissions = usePermissions();
  const { invoiceId } = financeInvoiceDocumentRoute.useParams();
  return (
    <InvoiceDocumentScreen
      gateway={financeGateway}
      invoiceId={invoiceId}
      canSend={permissions.includes('finance.invoice.send')}
      onBack={() => {
        void router.navigate({ to: '/finance/invoices/$invoiceId', params: { invoiceId } });
      }}
      onSend={() => {
        void router.navigate({ to: '/finance/invoices/$invoiceId/send', params: { invoiceId } });
      }}
    />
  );
}

/** Screen 32. */
function SendInvoiceRoute() {
  const router = useRouter();
  const { invoiceId } = financeInvoiceSendRoute.useParams();
  return (
    <SendInvoiceScreen
      gateway={financeGateway}
      invoiceId={invoiceId}
      onSent={() => {
        void router.navigate({ to: '/finance/deliveries' });
      }}
      onCancel={() => {
        void router.navigate({ to: '/finance/invoices/$invoiceId', params: { invoiceId } });
      }}
    />
  );
}

/** Screen 33. */
function DeliveryHistoryRoute() {
  const router = useRouter();
  const entityId = useFirstReachableEntity();
  if (entityId === undefined) return <LoadingState title="Loading…" />;
  return (
    <DeliveryHistoryScreen
      gateway={financeGateway}
      legalEntityId={entityId}
      onOpenInvoice={(invoiceId) => {
        void router.navigate({ to: '/finance/invoices/$invoiceId', params: { invoiceId } });
      }}
    />
  );
}

function CreditNotesRoute() {
  return <CreditNotesScreen gateway={financeGateway} />;
}

function ReceiptsRoute() {
  const permissions = usePermissions();
  return (
    <ReceiptsScreen
      gateway={financeGateway}
      canAllocate={permissions.includes('finance.receipt.allocate')}
    />
  );
}

function ReceivablesAgeingRoute() {
  const router = useRouter();
  return (
    <AgeingScreen
      gateway={financeGateway}
      side="receivables"
      onOpenParty={(partyId) => {
        void router.navigate({ to: '/finance/customers/$partyId', params: { partyId } });
      }}
    />
  );
}

function BillsRoute() {
  const router = useRouter();
  const permissions = usePermissions();
  return (
    <DesignerBillsScreen
      gateway={financeGateway}
      canEnter={permissions.includes('finance.bill.create')}
      onOpen={(billId) => {
        void router.navigate({ to: '/finance/bills/$billId', params: { billId } });
      }}
      onNew={() => {
        void router.navigate({ to: '/finance/bills/new' });
      }}
    />
  );
}

function BillRoute() {
  const { billId } = financeBillRoute.useParams();
  const permissions = usePermissions();
  return (
    <BillDetailScreen
      gateway={financeGateway}
      billId={billId}
      canApprove={permissions.includes('finance.bill.approve')}
      canSubmit={permissions.includes('finance.bill.create')}
      canPost={permissions.includes('finance.bill.post')}
    />
  );
}

function SupplierCreditsRoute() {
  return <SupplierCreditsScreen gateway={financeGateway} />;
}

function PaymentsRoute() {
  const permissions = usePermissions();
  return (
    <PaymentsScreen
      gateway={financeGateway}
      canAllocate={permissions.includes('finance.payment.allocate')}
    />
  );
}

function PayablesAgeingRoute() {
  return <AgeingScreen gateway={financeGateway} side="payables" />;
}

function AccountsRoute() {
  return <ChartOfAccountsScreen gateway={financeGateway} />;
}

function FinanceSetupRoute() {
  const permissions = usePermissions();
  return (
    <EntitySetupScreen
      gateway={financeGateway}
      canManage={permissions.includes('finance.setup.manage')}
    />
  );
}

function FinanceItemDefaultsRoute() {
  const entityId = useFirstReachableEntity();
  const permissions = usePermissions();
  if (entityId === undefined) return <LoadingState title="Loading item defaults…" />;
  return (
    <ItemDefaultsScreen
      gateway={financeGateway}
      catalogue={partiesGateway}
      legalEntityId={entityId}
      canManage={permissions.includes('finance.setup.manage')}
    />
  );
}

function PartiesRoute() {
  const permissions = usePermissions();
  return (
    <PartiesScreen
      gateway={partiesGateway}
      canManage={permissions.includes('platform.party.manage')}
    />
  );
}

function CatalogueRoute() {
  const permissions = usePermissions();
  return (
    <CatalogueScreen
      gateway={partiesGateway}
      canManage={permissions.includes('platform.catalogue.manage')}
    />
  );
}

function FinanceCodingRoute() {
  const entityId = useFirstReachableEntity();
  const permissions = usePermissions();
  if (entityId === undefined) return <LoadingState title="Loading coding setup…" />;
  return (
    <CodingSetupScreen
      gateway={financeGateway}
      legalEntityId={entityId}
      canManage={permissions.includes('finance.setup.manage')}
    />
  );
}

function FinanceRevaluationRoute() {
  const entityId = useFirstReachableEntity();
  const permissions = usePermissions();
  if (entityId === undefined) return <LoadingState title="Loading revaluations…" />;
  return (
    <RevaluationScreen
      gateway={financeGateway}
      legalEntityId={entityId}
      canRun={permissions.includes('finance.revaluation.run')}
    />
  );
}

function FinanceRecurringRoute() {
  const entityId = useFirstReachableEntity();
  const permissions = usePermissions();
  if (entityId === undefined) return <LoadingState title="Loading schedules…" />;
  return (
    <RecurringScreen
      gateway={financeGateway}
      legalEntityId={entityId}
      canGenerate={permissions.includes('finance.recurring.generate')}
      canManage={permissions.includes('finance.recurring.manage')}
    />
  );
}

function FinanceMatchRulesRoute() {
  const entityId = useFirstReachableEntity();
  const permissions = usePermissions();
  if (entityId === undefined) return <LoadingState title="Loading matching rules…" />;
  return (
    <MatchRulesScreen
      gateway={financeGateway}
      legalEntityId={entityId}
      canManage={permissions.includes('finance.match_rule.manage')}
    />
  );
}

function FinanceStandardReportsRoute() {
  const permissions = usePermissions();
  return (
    <FinanceReportsScreen
      gateway={financeGateway}
      canInstall={permissions.includes('finance.setup.manage')}
    />
  );
}

function CalendarRoute() {
  const entityId = useFirstReachableEntity();
  const permissions = usePermissions();
  if (entityId === undefined) return <LoadingState title="Loading Finance…" />;
  return (
    <FinancialCalendarScreen
      gateway={financeGateway}
      legalEntityId={entityId}
      canManage={permissions.includes('finance.period.manage')}
    />
  );
}

function JournalsRoute() {
  const router = useRouter();
  return (
    <JournalsScreen
      gateway={financeGateway}
      onOpen={(journalId) => {
        void router.navigate({ to: '/finance/journals/$journalId', params: { journalId } });
      }}
    />
  );
}

function JournalRoute() {
  const { journalId } = financeJournalRoute.useParams();
  return <JournalDetailScreen gateway={financeGateway} journalId={journalId} />;
}

function GeneralLedgerRoute() {
  return <GeneralLedgerScreen gateway={financeGateway} />;
}

function TrialBalanceRoute() {
  const entityId = useFirstReachableEntity();
  if (entityId === undefined) return <LoadingState title="Loading the trial balance…" />;
  return <TrialBalanceScreen gateway={financeGateway} legalEntityId={entityId} />;
}

function BankAccountsRoute() {
  const router = useRouter();
  return (
    <BankAccountsScreen
      gateway={financeGateway}
      onOpen={(bankAccountId) => {
        void router.navigate({
          to: '/finance/bank-accounts/$bankAccountId',
          params: { bankAccountId },
        });
      }}
    />
  );
}

function ReconciliationRoute() {
  const { bankAccountId } = financeReconciliationRoute.useParams();
  const permissions = usePermissions();
  return (
    <BankReconciliationScreen
      gateway={financeGateway}
      bankAccountId={bankAccountId}
      canReconcile={permissions.includes('finance.bank.reconcile')}
      canUnreconcile={permissions.includes('finance.bank.unreconcile')}
    />
  );
}

function VatPeriodsRoute() {
  const router = useRouter();
  const entityId = useFirstReachableEntity();
  const permissions = usePermissions();
  if (entityId === undefined) return <LoadingState title="Loading VAT…" />;
  return (
    <VatPeriodsScreen
      gateway={financeGateway}
      legalEntityId={entityId}
      canPrepare={permissions.includes('finance.vat.prepare')}
      onOpen={(vatReturnId) => {
        void router.navigate({ to: '/finance/vat/$vatReturnId', params: { vatReturnId } });
      }}
    />
  );
}

function VatReturnRoute() {
  const { vatReturnId } = financeVatReturnRoute.useParams();
  const permissions = usePermissions();
  return (
    <VatReturnScreen
      gateway={financeGateway}
      vatReturnId={vatReturnId}
      canSubmit={permissions.includes('finance.vat.submit')}
    />
  );
}
